import axios, { AxiosError } from 'axios';
import { ErrorSystemModel } from '../models/system/errorSystemModel';
import { ResponseErrorModel } from '../models/system/responseErrorModel';

export const createDefaultResponseError = (
  message = 'An unexpected error occurred. Please contact support.',
  error_code = 'UNEXPECTED_ERROR',
  error_event = 'unknown',
  error_location = 'unknown',
  critical = false,
  status = 500
): ResponseErrorModel => ({
  status,
  success: false,
  message,
  error: {
    error_code,
    error_event,
    error_location,
    critical
  }
});

export function handleAxiosError(error: any): ResponseErrorModel {
  try {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;

      if (axiosError.response) {
        const responseData = axiosError.response.data as ResponseErrorModel | undefined;

        if (responseData && responseData.error) {
          return responseData;
        } else {
          const statusCode = axiosError.response.status || 500;
          const isCritical = statusCode === 404;

          return createDefaultResponseError(axiosError.message || 'Error occurred', 'UNCAUGHT_ERROR', 'axios-error', 'handleAxiosError', isCritical, statusCode);
        }
      }
    }

    return createDefaultResponseError(error.message || 'Uncaught Error', 'UNCAUGHT_ERROR', 'uncaught-error', 'handleAxiosError');
  } catch (error) {
    return createDefaultResponseError('An error occurred while processing the error', 'INTERNAL_ERROR', 'internal-error', 'handleAxiosError');
  }
}

export const mapResponseErrorToSystemError = (error: ResponseErrorModel): ErrorSystemModel => {
  return {
    error_code: error.error?.error_code || 'UNKNOWN_ERROR', // Fallback for error_code
    error_event: error.error?.error_event || 'Error Handling Event', // Default event name
    error_location: error.error?.error_location || 'Unknown Location', // Fallback for location
    critical: error.error?.critical || false, // Determine if it's a critical error
    error_message: error.message || 'An unexpected error occurred.', // Default message
    error_detail: error.error?.error_detail || '', // Use error detail if available
    error_http: error.error.error_http || 500, // Use the HTTP status if available
  };
};

export const mapUnknownErrorToSystemError = (error: unknown, critical: boolean = false): ErrorSystemModel => {
  return {
    error_code: 'UNKNOWN_ERROR',
    error_event: 'Unknown Error Handling',
    error_location: 'fetchVerifyNewEmail', // Provide location for debugging
    critical, // Use the passed critical status
    error_message: error instanceof Error ? error.message : 'An unexpected error occurred.',
    error_detail: '', // Optionally include more detail
    error_http: 500, // Default HTTP status
  };
};

export const handleError = (
  error: ErrorSystemModel,
  setErrorMessage: ((message: string | null) => void) | null,
  setDidErrorOccur: (occurred: boolean) => void,
  triggerSystemError: (error: ErrorSystemModel) => void,
  defaultMessage: string = 'An unexpected error occurred.'
): void => {
  
  if (error.critical) {
    triggerSystemError(error);
    setDidErrorOccur(true);
  } else if (setErrorMessage) {
    let error_message: string = (error.error_message ?? 'Default error message').replace(/^\s*\(\s*|\s*\)\s*$/g, '');

    try {
      const parsedError = JSON.parse(error_message);

      if (Array.isArray(parsedError)) {
        error_message = parsedError.join('\n');
      }
    } catch {
      error_message = error.error_message || defaultMessage;
    }

    setErrorMessage(error_message);
  }
  else {
    triggerSystemError(error);
  }
};

